import { useEffect, useState } from 'react'

import { useForm as useConformForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { useParams } from '@remix-run/react'

import { MultiSelect } from '~/components'
import {
  CheckBoxOptions,
  useAreasForm,
  useFacilitiesForm,
  useKeywordForm,
  useMonthlyCostsForm,
  useNumberOfUsersForm,
  useOfficeTypeForm,
} from '~/features/common'

import {
  FieldType,
  FormValues,
  searchFormSchema,
  SearchFormType,
} from './search-form'

export const useWatchFormValues = (fields: FieldType) => {
  const areas = Array.isArray(fields.areas.value)
    ? fields.areas.value.filter((val): val is string => val !== undefined)
    : []
  const officeTypes = Array.isArray(fields.officeTypes.value)
    ? fields.officeTypes.value.filter((val): val is string => val !== undefined)
    : []
  const facilities = Array.isArray(fields.facilities.value)
    ? fields.facilities.value.filter((val): val is string => val !== undefined)
    : []
  const minPeople = fields.minPeople.value || ''
  const minMonthlyRent = fields.minMonthlyRent.value || ''
  const maxMonthlyRent = fields.maxMonthlyRent.value || ''
  const keyword = fields.keyword.value || ''

  const [watchedFormValues, setWatchedFormValues] = useState<FormValues>({
    areas,
    officeTypes,
    facilities,
    minPeople,
    minMonthlyRent,
    maxMonthlyRent,
    keyword,
  })

  const changeWatchedFormValues = (formData: FormValues) => {
    setWatchedFormValues((prevState) => ({
      ...prevState,
      ...formData,
    }))
  }

  const changeWatchedArea = (areas: string[]) => {
    changeWatchedFormValues?.({ areas: areas })
  }
  const changeWatchedOfficeTypes = (officeTypes: string[]) => {
    changeWatchedFormValues?.({ officeTypes: officeTypes })
  }
  const changeWatchedMinPeople = (minPeople: string) => {
    changeWatchedFormValues?.({ minPeople: minPeople })
  }
  const changeWatchedMinMonthlyRent = (minMonthlyRent: string) => {
    changeWatchedFormValues?.({ minMonthlyRent: minMonthlyRent })
  }
  const changeWatchedMaxMonthlyRent = (maxMonthlyRent: string) => {
    changeWatchedFormValues?.({ maxMonthlyRent: maxMonthlyRent })
  }
  const changeWatchedFacilities = (facilities: string[]) => {
    changeWatchedFormValues?.({ facilities: facilities })
  }
  const changeWatchedKeyword = (keyword: string) => {
    changeWatchedFormValues?.({ keyword: keyword })
  }

  return {
    watchedFormValues,
    changeWatchedFormValues,
    changeWatchedArea,
    changeWatchedOfficeTypes,
    changeWatchedMinPeople,
    changeWatchedMinMonthlyRent,
    changeWatchedMaxMonthlyRent,
    changeWatchedFacilities,
    changeWatchedKeyword,
  }
}

const convertToQueryParams = (data: FormValues): string => {
  const params = new URLSearchParams()
  Object.entries(data).forEach(([key, value]) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return
    }

    if (Array.isArray(value)) {
      params.append(key, value.join(','))
      return
    }

    params.append(key, value.toString())
  })

  return params.toString()
}

export const useSearchHitCount = (formValues: FormValues) => {
  const [searchHitCount, setSearchHitCount] = useState(0)
  const changeSearchHitCount = (count: number) => {
    setSearchHitCount(count)
  }

  useEffect(() => {
    const queryParams = convertToQueryParams(formValues)
    fetch(`/api/search/offices/?${queryParams}`, {
      method: 'GET',
    })
      .then((response) => response.json())
      .then((data) => {
        changeSearchHitCount(data.count)
      })
  }, [formValues])

  return { searchHitCount }
}

const useForm = (initialValues: FormValues, onSubmit?: () => void) => {
  const { prefectureSlug } = useParams()
  const [form, fields] = useConformForm<SearchFormType>({
    id: 'searchForm',
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: searchFormSchema })
    },
    onSubmit() {
      onSubmit?.()
    },
    defaultValue: initialValues,
  })

  return {
    form,
    fields,
    prefectureSlug: prefectureSlug || '',
  }
}

type Options = React.ComponentProps<typeof MultiSelect>['options']

export const useSearchFormContext = (
  initialValues: FormValues,
  areaFormOptions: Options,
  officeTypeFormOptions: Options,
  facilitiesAndServicesFormOptions: CheckBoxOptions,
  onSubmit?: () => void,
) => {
  const { form, fields, prefectureSlug } = useForm(initialValues, onSubmit)
  // OPTIMIZE: fields.areas.valueではフォームの値の変更を検知の挙動が安定しないため、変更検知用の別にフォームの現在の値を保持するstateを作成した。冗長なので将来的にはconformが提供する値に置き換えたい。
  const {
    watchedFormValues,
    changeWatchedArea,
    changeWatchedFacilities,
    changeWatchedKeyword,
    changeWatchedOfficeTypes,
    changeWatchedMinPeople,
    changeWatchedMinMonthlyRent,
    changeWatchedMaxMonthlyRent,
  } = useWatchFormValues(fields)
  const {
    value: areasValue,
    onChange: onChangeAreas,
    name: areasInputName,
  } = useAreasForm(fields.areas, changeWatchedArea)
  const {
    value: officeTypesValue,
    onChange: onChangeOfficeTypes,
    name: officeTypeInputName,
  } = useOfficeTypeForm(fields.officeTypes, changeWatchedOfficeTypes)
  const {
    value: minPeopleValue,
    onChange: onChangeMinPeople,
    name: minPeopleInputName,
  } = useNumberOfUsersForm(fields.minPeople, changeWatchedMinPeople)
  const {
    minMonthlyRentValue,
    maxMonthlyRentValue,
    onChangeMinMonthlyRent,
    onChangeMaxMonthlyRent,
    minMonthlyRentName,
    maxMonthlyRentName,
    onBlurMinMonthlyRent,
    onBlurMaxMonthlyRent,
  } = useMonthlyCostsForm(
    fields.minMonthlyRent,
    fields.maxMonthlyRent,
    changeWatchedMinMonthlyRent,
    changeWatchedMaxMonthlyRent,
  )
  const {
    value: facilitiesValue,
    onChange: onChangeFacilities,
    name: facilitiesInputName,
  } = useFacilitiesForm(fields.facilities, changeWatchedFacilities)
  const {
    value: keywordValue,
    onChange: onChangeKeyword,
    name: keywordInputName,
    onEnterKeyDown: onEnterKeyword,
    onBlur: onBlurKeyword,
  } = useKeywordForm(fields.keyword, changeWatchedKeyword)

  return {
    form,
    fields,
    prefectureSlug,
    areaFormOptions,
    officeTypeFormOptions,
    facilitiesAndServicesFormOptions,
    watchedFormValues,
    areasValue,
    onChangeAreas,
    areasInputName,
    officeTypesValue,
    onChangeOfficeTypes,
    officeTypeInputName,
    minPeopleValue,
    minPeopleInputName,
    onChangeMinPeople,
    minMonthlyRentValue,
    maxMonthlyRentValue,
    onChangeMinMonthlyRent,
    onChangeMaxMonthlyRent,
    minMonthlyRentName,
    maxMonthlyRentName,
    facilitiesValue,
    onChangeFacilities,
    facilitiesInputName,
    keywordValue,
    onChangeKeyword,
    keywordInputName,
    onEnterKeyword,
    onBlurKeyword,
    onBlurMinMonthlyRent,
    onBlurMaxMonthlyRent,
  }
}
